import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import classes from './TranslatedContent.module.scss';

// Configure worker.
// The scheme is pinned to https rather than left protocol-relative so the worker
// can never be fetched over plain http, and so a CSP can name a single concrete
// origin (worker-src/script-src https://unpkg.com) for it.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


const TranslatedContent = forwardRef(({ pdfUrl, loadingForTranslatedAuction, handleShowTranslated }, ref) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [error, setError] = useState(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const pdfRef = useRef(null);

    useImperativeHandle(ref, () => ({
        scrollToPdf: () => {
            if (pdfRef.current) {
                const rect = pdfRef.current.getBoundingClientRect();
                window.scrollTo(0, window.scrollY + rect.top - 100);
            }
        }
    }));

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Calculate PDF width based on screen size
    const getPdfWidth = () => {
        if (windowWidth < 480) return windowWidth - 32; // smaller mobile
        if (windowWidth < 768) return windowWidth - 40; // larger mobile
        if (windowWidth < 1024) return 600; // tablet
        return 800; // desktop
    };

    const onDocumentLoadSuccess = () => {
        setError(null);
    };

    const onDocumentLoadError = (error) => {
        console.error('Error loading PDF:', error);
        setError('Failed to load PDF file. Please try again.');
    };

    // Handle Escape key press
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isModalOpen) {
                setIsModalOpen(false);
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isModalOpen]);

    // Prevent body scroll when modal is open. The page scrolls on body, so only touch its overflow
    // while the modal is up: clearing it hands scrolling to html, and the navbar scrolls away with the page.
    useEffect(() => {
        if (!isModalOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isModalOpen]);

    // Handle keyboard interaction for thumbnail
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            setIsModalOpen(true);
        }
    };

    return (
        <>
            <div className={classes.DetailContent}>
                <div className={classes.inspectionGallery} ref={pdfRef}>
                    <div className={classes.headerContainer}>
                    
                        <h2>Translated Auction Sheet</h2>
                        <button 
                            className={classes.closeButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                // BODY is the scrolling element in this app (see index.scss)
                                document.body.scrollTo(0, 500);
                                window.scrollTo(0, 500);

                                setTimeout(() => {
                                    handleShowTranslated();
                                }, 300);
                            }}
                            aria-label="Close translated content"
                        >
                            ×
                        </button>
                    </div>
                    <div className={classes.dataContainer}>
                        <div 
                            className={classes.pdfContainer} 
                            onClick={() => setIsModalOpen(true)}
                            onKeyPress={handleKeyPress}
                            role="button"
                            tabIndex="0"
                            aria-label="View translated content in full screen"
                        >
                            {error ? (
                                <div className={classes.errorMessage}>{error}</div>
                            ) : loadingForTranslatedAuction ? (
                                    <div className={classes.loading}>Loading PDF...</div>
                                )
                                : (
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={onDocumentLoadError}
                                    loading={<div className={classes.loading}>Loading PDF...</div>}
                                    className={classes.pdfDocument}
                                >
                                    <Page 
                                        pageNumber={1}
                                        width={getPdfWidth()}
                                        className={classes.pdfPage}
                                    />
                                </Document>
                                
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div 
                    className={classes.modal} 
                    onClick={() => setIsModalOpen(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div 
                        className={classes.modalContent} 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button 
                            className={classes.modalCloseButton}
                            onClick={() => setIsModalOpen(false)}
                            aria-label="Close translated content modal"
                        >
                            ×
                        </button>
                        <Document
                            file={pdfUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={<div className={classes.loading}>Loading PDF...</div>}
                            className={classes.pdfDocument}
                        >
                            <Page 
                                pageNumber={1}
                                width={getPdfWidth()}
                                className={classes.pdfPage}
                            />
                        </Document>
                    </div>
                </div>
            )}
        </>
    );
});


export default TranslatedContent;